import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Skeleton,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  NotificationsNone as NotificationsNoneIcon,
  FiberManualRecord as LiveDotIcon,
} from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationContext';
import {
  Notification,
  NotificationType,
  NewFollowerMetadata,
  LiveStreamStartedMetadata,
} from '../types/notification';
import { groupNotificationsByDate, GroupedNotifications } from '../utils/notificationUtils';

// ─── Type-Specific Notification Renderers ───────────────────────────────────

interface NotificationItemProps {
  notification: Notification;
  onActivate: () => void;
}

/**
 * Renderer for `new_follower` notifications.
 * Displays avatar, display name, and "followed you" text.
 */
const NewFollowerItem: React.FC<NotificationItemProps> = ({ notification, onActivate }) => {
  const meta = notification.metadata as NewFollowerMetadata;

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', cursor: 'pointer' }}
      onClick={onActivate}
    >
      <Avatar
        src={meta.profilePicture}
        alt={meta.displayName}
        sx={{ width: 48, height: 48 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1" noWrap>
          <Typography component="span" fontWeight={600}>
            {meta.displayName}
          </Typography>{' '}
          followed you
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatRelativeTime(notification.createdAt)}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Renderer for `live_stream_started` notifications.
 * Displays avatar, display name, stream title, and a "LIVE" badge.
 */
const LiveStreamItem: React.FC<NotificationItemProps> = ({ notification, onActivate }) => {
  const meta = notification.metadata as LiveStreamStartedMetadata;

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', cursor: 'pointer' }}
      onClick={onActivate}
    >
      <Avatar
        src={meta.profilePicture}
        alt={meta.displayName}
        sx={{ width: 48, height: 48 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" noWrap sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="span" fontWeight={600}>
              {meta.displayName}
            </Typography>{' '}
            {meta.streamTitle}
          </Typography>
          <Chip
            icon={<LiveDotIcon sx={{ fontSize: 10 }} />}
            label="LIVE"
            size="small"
            color="error"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 700,
              '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          is live now
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Extensible renderer map — add new notification types here.
 * Each entry maps a NotificationType to a React component and a navigation target.
 */
const NOTIFICATION_RENDERERS: Record<
  NotificationType,
  {
    Component: React.FC<NotificationItemProps>;
    getNavigationPath: (notification: Notification) => string;
  }
> = {
  new_follower: {
    Component: NewFollowerItem,
    getNavigationPath: (n) => `/profile/${(n.metadata as NewFollowerMetadata).userId}`,
  },
  live_stream_started: {
    Component: LiveStreamItem,
    getNavigationPath: (n) =>
      `/live/watch/${(n.metadata as LiveStreamStartedMetadata).streamId}`,
  },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Simple relative-time formatter. */
function formatRelativeTime(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(isoDate).toLocaleDateString();
}

// ─── Sub-Components ─────────────────────────────────────────────────────────

/** Renders a single notification item with unread highlight and keyboard support. */
const NotificationRow: React.FC<{
  notification: Notification;
  onNavigate: (path: string) => void;
}> = ({ notification, onNavigate }) => {
  const theme = useTheme();
  const renderer = NOTIFICATION_RENDERERS[notification.type];

  if (!renderer) return null;

  const { Component, getNavigationPath } = renderer;
  const path = getNavigationPath(notification);

  const handleActivate = () => onNavigate(path);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate();
    }
  };

  return (
    <Box
      role="listitem"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      sx={{
        px: 2,
        py: 1.5,
        borderLeft: notification.read
          ? '4px solid transparent'
          : `4px solid ${theme.palette.primary.main}`,
        backgroundColor: notification.read
          ? 'transparent'
          : alpha(theme.palette.primary.main, 0.04),
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: alpha(theme.palette.action.hover, 0.08),
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
        cursor: 'pointer',
      }}
    >
      <Component notification={notification} onActivate={handleActivate} />
    </Box>
  );
};

/** Renders a date section header. */
const DateSectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <Typography
    variant="overline"
    component="div"
    sx={{
      px: 2,
      pt: 2,
      pb: 0.5,
      color: 'text.secondary',
      fontWeight: 700,
      letterSpacing: 1.2,
    }}
  >
    {label}
  </Typography>
);

/** Skeleton placeholder for a single notification row. */
const NotificationSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5 }}>
    <Skeleton variant="circular" width={48} height={48} />
    <Box sx={{ flex: 1 }}>
      <Skeleton variant="text" width="60%" height={24} />
      <Skeleton variant="text" width="30%" height={18} />
    </Box>
  </Box>
);

/** Empty state illustration. */
const EmptyState: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '40vh',
      textAlign: 'center',
      px: 3,
    }}
  >
    <NotificationsNoneIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
    <Typography variant="h6" gutterBottom>
      No notifications yet
    </Typography>
    <Typography variant="body2" color="text.secondary">
      When someone follows you or goes live, you&apos;ll see it here.
    </Typography>
  </Box>
);

// ─── Main Page Component ────────────────────────────────────────────────────

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    notifications,
    isLoading,
    hasMore,
    fetchNotifications,
    markAllAsRead,
  } = useNotifications();

  // Track previous notification count for ARIA live announcements
  const prevCountRef = useRef((notifications || []).length);
  const [newNotificationAnnouncement, setNewNotificationAnnouncement] = useState('');

  // ── Mark all as read on mount ───────────────────────────────────────────
  const hasMarkedRef = useRef(false);
  useEffect(() => {
    if (!hasMarkedRef.current) {
      hasMarkedRef.current = true;
      markAllAsRead();
    }
  }, [markAllAsRead]);

  // ── Fetch initial notifications on mount ────────────────────────────────
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchNotifications(true);
    }
  }, [fetchNotifications]);

  // ── ARIA live region: announce new notifications ────────────────────────
  const safeNotifications = notifications || [];
  useEffect(() => {
    const currentCount = safeNotifications.length;
    const prevCount = prevCountRef.current;
    if (currentCount > prevCount && prevCount > 0) {
      const newCount = currentCount - prevCount;
      const noun = newCount === 1 ? 'notification' : 'notifications';
      setNewNotificationAnnouncement(`${newCount} new ${noun}`);
    }
    prevCountRef.current = currentCount;
  }, [safeNotifications.length]);

  // ── Infinite scroll via IntersectionObserver ────────────────────────────
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !isLoading) {
        fetchNotifications(false);
      }
    },
    [hasMore, isLoading, fetchNotifications],
  );

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(handleIntersect, {
      rootMargin: '200px',
    });

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [handleIntersect]);

  // ── Navigation handler ──────────────────────────────────────────────────
  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate],
  );

  // ── Group notifications by date ─────────────────────────────────────────
  const grouped: GroupedNotifications = groupNotificationsByDate(safeNotifications);

  // ── Determine what to render ────────────────────────────────────────────
  const isInitialLoad = isLoading && safeNotifications.length === 0;
  const isEmpty = !isLoading && safeNotifications.length === 0;

  // ── Render date sections ────────────────────────────────────────────────
  const renderSection = (label: string, items: Notification[]) => {
    if (items.length === 0) return null;
    return (
      <React.Fragment key={label}>
        <DateSectionHeader label={label} />
        {items.map((n) => (
          <NotificationRow key={n.id} notification={n} onNavigate={handleNavigate} />
        ))}
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ py: isMobile ? 1 : 3, width: '100%', maxWidth: 700, mx: 'auto' }}>
      {/* Page header */}
      <Typography
        variant="h5"
        component="h1"
        sx={{ px: 2, pt: 1, pb: 2, fontWeight: 700 }}
      >
        Notifications
      </Typography>

      {/* ARIA live region for new notification announcements */}
      <Box
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
        }}
      >
        {newNotificationAnnouncement}
      </Box>

      {/* Initial loading skeletons */}
      {isInitialLoad && (
        <Box role="list" aria-label="Loading notifications">
          {Array.from({ length: 6 }).map((_, i) => (
            <NotificationSkeleton key={i} />
          ))}
        </Box>
      )}

      {/* Empty state */}
      {isEmpty && <EmptyState />}

      {/* Notification list */}
      {!isInitialLoad && !isEmpty && (
        <Box role="list" aria-label="Notifications">
          {renderSection('Today', grouped.today)}
          {renderSection('Yesterday', grouped.yesterday)}
          {renderSection('Earlier', grouped.earlier)}

          {/* Loading more indicator */}
          {isLoading && (
            <Box sx={{ py: 2 }}>
              <NotificationSkeleton />
              <NotificationSkeleton />
            </Box>
          )}

          {/* Sentinel element for infinite scroll */}
          <Box ref={sentinelRef} sx={{ height: 1 }} aria-hidden="true" />
        </Box>
      )}
    </Box>
  );
};

export default NotificationsPage;
